import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { AuthClient } from '../authClient';
import {
  RegisterDto,
  LoginDto,
  CheckIdentifierRequestDto,
  TenantSelectDto,
  TwoFactorDto,
  RefreshTokenDto,
  LoginResponseDto,
  LogoutResponse,
  MeDto,
  ApiResponse,
  ForgetPasswordDto,
  ResetPasswordDto,
} from '../types/auth.types';

export const useRegisterMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RegisterDto) => AuthClient.register(data),
    onSuccess: () => {
      toast.success('Registration successful');
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Registration failed.');
    },
  });
};

export const useLoginMutation = () => {
  console.log('Login mutation triggered');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: LoginDto) => AuthClient.login(data),
    onSuccess: (response: ApiResponse<LoginResponseDto>) => {
      if (!response?.data?.requiresTwoFactor) {
        toast.success('Login successful');
        queryClient.invalidateQueries({ queryKey: ['auth'] });
      }
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Login failed.');
    },
  });
};

/**
 * Hook for Step 1 of two-step login: Check if email/phone has associated tenants
 */
export const useCheckIdentifierMutation = () => {
  return useMutation({
    mutationFn: (data: CheckIdentifierRequestDto) => AuthClient.checkIdentifier(data),
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'No accounts found with this identifier.');
    },
  });
};

export const useResetPasswordMutation = () => {
  return useMutation({
    mutationFn: (data: ResetPasswordDto) => AuthClient.resetPassword(data),
    onSuccess: () => {
      toast.success('Password reset successfully');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Reset failed');
    },
  });
};

export const useVerifyTwoFactorMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TwoFactorDto) => AuthClient.verifyTwoFactor(data),
    onSuccess: () => {
      toast.success('Two-factor verification successful');
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Two-factor verification failed.');
    },
  });
};

export const useForgetPasswordMutation = () => {
  return useMutation({
    mutationFn: (data: ForgetPasswordDto) => AuthClient.forgetPassword(data),
    onSuccess: () => {
      toast.success('OTP sent successfully to your phone');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Failed to send OTP');
    },
  });
};

export const useRefreshMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RefreshTokenDto) => AuthClient.refresh(data),
    onSuccess: () => {
      toast.success('Token refreshed successfully');
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Token refresh failed.');
    },
  });
};

export const useLogoutMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => AuthClient.logout(),
    onSuccess: () => {
      toast.success('Logged out successfully');
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Logout failed.');
    },
  });
};

export const useGetActiveSessionsQuery = (userId: string) => {
  return useQuery<ApiResponse<number>>({
    queryKey: ['auth', 'sessions', userId],
    queryFn: () => AuthClient.getActiveSessions(userId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
};

export const useRevokeAllSessionsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => AuthClient.revokeAllSessions(userId),
    onSuccess: () => {
      toast.success('All sessions revoked successfully');
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Failed to revoke sessions.');
    },
  });
};

export const useCurrentUserQuery = (options: { enabled?: boolean } = {}) => {
  return useQuery<ApiResponse<MeDto>>({
    queryKey: ['auth', 'me'],
    queryFn: () => AuthClient.getCurrentUser(),
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled: options.enabled ?? true,
  });
};
